import { Box, Button, Typography } from "@mui/material";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_PaginationState,
} from "material-react-table";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import useGetDomesticBranches from "src/shared/data-access/business/useGetDomesticBranches";
import useGetDomesticBranchesParams from "src/shared/data-access/business/useGetDomesticBranchesParams";
import i18n from "src/shared/services/languages/i18n";
import getTableLang from "src/shared/services/languages/MaterialReactTable/getTableLang";
import { TDomesticBranches } from "src/business/types";
import useCustomMaterialTable from "src/shared/hooks/useCustomMaterialTable";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

function DomesticBranches() {
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data, isLoading } = useGetDomesticBranches(pagination);

  const { data: paramData, isLoading: isParamLoading } =
    useGetDomesticBranchesParams();

  const newBranchApp = paramData?.data?.availableApplicationTypes?.find(
    (a) => a.action === "new_branch",
  );
  const editBranchApp = paramData?.data?.availableApplicationTypes?.find(
    (a) => a.action === "edit_branch",
  );
  const deleteBranchApp = paramData?.data?.availableApplicationTypes?.find(
    (a) => a.action === "delete_branch",
  );

  const columns = useMemo<MRT_ColumnDef<TDomesticBranches>[]>(
    () => [
      {
        header: t("Αριθμός ΓΕΜΗ υποκαταστήματος"),
        accessorKey: "company_id",
      },
      {
        header: t("Νομός"),
        accessorKey: "prefecture",
      },
      {
        header: t("Διεύθυνση υποκαταστήματος"),
        accessorKey: "address",
      },
      {
        header: t("Ενέργειες"),
        Cell: ({ row }) => (
          <Box display="flex" flexDirection="column" gap={2}>
            {editBranchApp && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<EditIcon />}
                onClick={() => {
                  const selectedBranch = {
                    company_id: row?.original?.company_id,
                    co_name: row?.original?.co_name,
                  };
                  localStorage.setItem(
                    "selectedBranch",
                    JSON.stringify(selectedBranch),
                  );
                  navigate(`/application/${editBranchApp.m00_id}/index`);
                }}
              >
                {t("Υποβολή αίτησης για μεταβολή")}
              </Button>
            )}

            {deleteBranchApp && (
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => {
                  const selectedBranch = {
                    company_id: row?.original?.company_id,
                    co_name: row?.original?.co_name,
                  };

                  localStorage.setItem(
                    "selectedBranch",
                    JSON.stringify(selectedBranch),
                  );
                  navigate(`/application/${deleteBranchApp.m00_id}/index`);
                }}
              >
                {t("Υποβολή αίτησης για διαγραφή")}
              </Button>
            )}
          </Box>
        ),
      },
    ],
    [i18n.language, paramData?.data?.availableApplicationTypes],
  );
  const table = useCustomMaterialTable({
    columns,
    data: data?.data?.data ?? [],
    localization: getTableLang(),
    enableTopToolbar: false,
    enableColumnActions: false,
    onPaginationChange: setPagination,
    enableSorting: false,
    manualPagination: true,
    rowCount: data?.data?.recordsTotal,
    state: {
      pagination,
      isLoading,
    },
  });
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "2rem",
        width: "80vw",
        mx: "auto",
      }}
    >
      <Typography variant="h4">
        {t(
          "Υποκαταστήματα στην Ημεδαπή που είναι καταχωρισμένα στην μερίδα της επιχείρησης",
        )}
      </Typography>
      <Typography variant="body1">
        {t("Εμφανίζονται τα υποκαταστήματα της επιχείρησης")}
      </Typography>
      <Box sx={{ maxWidth: "80vw" }}>
        <MaterialReactTable table={table} />
      </Box>
      {newBranchApp && (
        <Button
          variant="contained"
          sx={{ alignSelf: "start" }}
          onClick={() => navigate(`/application/${newBranchApp.m00_id}/index`)}
        >
          {t("Υποβολή αίτησης για νέο υποκατάστημα Ημεδαπής")}
        </Button>
      )}
    </Box>
  );
}

export default DomesticBranches;
